'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';

import { useEffect, useId, useRef, useState } from 'react';
import './elina-work-progress.css';

type WorkKind = 'interview' | 'proposal' | 'brief';
type WorkMessage = readonly [string, string];

// These are reassuring descriptions of the pending operation, not server
// milestones. No elapsed time implies completion or a percentage of progress.
const SOURCE_MESSAGES: Record<WorkKind, readonly WorkMessage[]> = {
  interview: [
    ['Je relis votre demande.', 'Pour bien comprendre ce que vous souhaitez améliorer.'],
    ['Je prépare une réponse adaptée.', 'Votre objectif reste au cœur de la réponse.'],
    ['Vos précisions guident ma réponse.', 'En tenant compte de votre activité et de vos besoins.'],
    ['Je prépare la suite de notre échange.', 'Vous pourrez vérifier ma compréhension et préciser vos choix.'],
  ],
  proposal: [
    ['Je prépare votre proposition.', 'À partir de votre ambition et des choix retenus.'],
    ['Vos choix guident la proposition.', 'Le périmètre que vous avez choisi reste la référence.'],
    ['Votre ambition reste au premier plan.', 'Des solutions, des résultats concrets et des bénéfices pour votre activité.'],
    ['Je prépare une proposition à relire.', 'Vous pourrez la vérifier et l’ajuster avant de la transmettre.'],
  ],
  brief: [
    ['Je lis votre document.', 'Pour comprendre le besoin que vous souhaitez partager.'],
    ['Votre brief est en cours d’analyse.', 'Les informations de votre fichier servent à préparer la synthèse.'],
    ['Je prépare une synthèse à vérifier.', 'Vous pourrez corriger ce que j’ai compris avant de poursuivre.'],
    ['Je poursuis l’analyse de votre brief.', 'Votre document est toujours en cours de traitement.'],
  ],
};
const TRACKS = [
  'M218 324C277 306 327 345 327 398C327 440 296 465 240 473L316 630C334 674 360 695 401 703',
  'M407 535L408 459C486 457 531 477 553 521C580 575 562 637 522 674C480 712 423 711 377 683',
  'M416 208C457 171 496 181 524 237C546 280 570 361 609 460L681 616C693 644 708 644 720 613L853 254',
];
const random = (min: number, max: number) => min + Math.random() * (max - min);

export default function ElinaWorkProgress({ kind, request, onCancel, hasReference }: {
  kind: WorkKind;
  request?: string;
  onCancel?: () => void;
  hasReference?: boolean;
}) {
  const { t, href, content, locale } = useI18n();
  const MESSAGES = content(SOURCE_MESSAGES);

  const uniqueId = 'elina-work-' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const svgRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [longWait, setLongWait] = useState(false);

  useEffect(() => {
    setMessageIndex(0); setLongWait(false);
    let elapsed = 0;
    let lastTick = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const delta = Math.min(Math.max(now - lastTick, 0), 1000);
      lastTick = now;
      if (document.hidden) return;
      elapsed += delta;
      setMessageIndex(Math.min(Math.floor(elapsed / 7000), MESSAGES[kind].length - 1));
      if (elapsed >= 45000) setLongWait(true);
    }, 500);
    const resetClock = () => { lastTick = performance.now(); };
    document.addEventListener('visibilitychange', resetClock);
    const panel = panelRef.current;
    if (panel) {
      const bounds = panel.getBoundingClientRect();
      if (bounds.bottom > window.innerHeight || bounds.top < 100) {
        panel.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
    }
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', resetClock); };
  }, [kind]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const paths = Array.from(svg.querySelectorAll<SVGPathElement>('[data-work-track]'));
    const particles = Array.from(svg.querySelectorAll<SVGGElement>('[data-work-particle]')).map((group, index) => {
      const path = paths[index % paths.length];
      const length = path.getTotalLength();
      const lane = Math.floor(index / paths.length);
      return { group, path, length, pos: (lane + random(.15, .85)) / 2 * length, speed: random(24, 60) * 2.6, direction: Math.random() > .5 ? 1 : -1, phase: random(0, Math.PI * 2) };
    });
    let frame: number | null = null;
    let lastFrame = performance.now();
    const paint = (dt: number, now: number) => {
      for (const p of particles) {
        p.pos += p.direction * p.speed * dt;
        if (p.pos > p.length || p.pos < 0) {
          p.pos = Math.max(0, Math.min(p.length, p.pos));
          p.direction *= -1;
          p.speed = random(24, 60) * 2.6;
        }
        const point = p.path.getPointAtLength(p.pos);
        p.group.setAttribute('transform', `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)})`);
        p.group.setAttribute('opacity', (motion.matches ? .95 : .87 + .13 * Math.sin(now / 1700 + p.phase)).toFixed(3));
      }
    };
    const animate = (now: number) => {
      frame = null;
      if (document.hidden || motion.matches) return;
      paint(Math.min(Math.max((now - lastFrame) / 1000, 0), .05), now);
      lastFrame = now;
      frame = requestAnimationFrame(animate);
    };
    const sync = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      lastFrame = performance.now();
      paint(0, lastFrame);
      if (!document.hidden && !motion.matches) frame = requestAnimationFrame(animate);
    };
    sync();
    motion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const message: WorkMessage = longWait
    ? [kind === 'brief' ? t('Je poursuis l’analyse de votre brief.') : t('Je poursuis la préparation.'), t('Votre demande est toujours en cours de traitement.')]
    : hasReference && kind !== 'brief' && messageIndex === 0
      ? [t('J’examine votre demande et sa référence.'), t('Les fonctions documentées aideront à préparer une réponse adaptée à votre activité.')]
      : MESSAGES[kind][messageIndex];

  return <div ref={panelRef} className="elina-work-progress" data-work-kind={kind}>
    {request && <div className="elina-work-request"><small>{kind === 'brief' ? t('Votre fichier') : t('Votre demande')}</small><p>{request}</p></div>}
    <div className="elina-work-panel">
      <div className="elina-work-caption" aria-hidden="true"><span>{kind === 'brief' ? t('Votre brief prend forme') : t('Votre projet prend forme')}</span><span className="elina-work-badge"><i />{t("En cours")}</span></div>
      <div className="elina-work-logo" aria-hidden="true">
        <svg ref={svgRef} viewBox="168 140 740 596" width="260" height="210" preserveAspectRatio={"xMidYMid meet"} focusable="false">
          <defs>
            <image id={`${uniqueId}-image`} href={href("/assets/universes/logo.png")} width="1024" height="1024" />
            <clipPath id={`${uniqueId}-clip`} clipPathUnits="userSpaceOnUse"><rect x="168" y="140" width="740" height="596" /></clipPath>
            <g id={`${uniqueId}-letters`} clipPath={`url(#${uniqueId}-clip)`}><use href={href(`#${uniqueId}-image`)} /></g>
            <mask id={`${uniqueId}-surface`} maskUnits="userSpaceOnUse" x="168" y="140" width="740" height="596" style={{ maskType: 'alpha' }}><use href={href(`#${uniqueId}-letters`)} /></mask>
            <radialGradient id={`${uniqueId}-glow`}><stop offset="0" stopColor="#ffffff" /><stop offset=".2" stopColor="#e8ffdf" /><stop offset=".45" stopColor="#c4ffbc" stopOpacity=".7" /><stop offset="1" stopColor="#a3f4cf" stopOpacity="0" /></radialGradient>
          </defs>
          <use href={href(`#${uniqueId}-letters`)} />
          {TRACKS.map(d => <path key={d} data-work-track d={d} fill="none" stroke="none" />)}
          <g mask={`url(#${uniqueId}-surface)`}>
            {Array.from({ length: 6 }, (_, i) => <g key={i} data-work-particle opacity="0"><circle r={36.4 + i % 3 * 5.2} fill={`url(#${uniqueId}-glow)`} /><circle r={8.32 + i % 3 * 1.17} fill="#f1ffe8" /><circle r="4.29" fill="#ffffff" /></g>)}
          </g>
        </svg>
      </div>
      <div className="elina-work-message" role="status" aria-live="polite" aria-atomic="true">
        <div key={message[0]} className="elina-work-message-text"><h3>{message[0]}</h3><p>{message[1]}</p></div>
      </div>
    </div>
    <p className="elina-work-reassurance">{t("Votre demande est bien prise en compte.")}</p>
    {onCancel && <button type="button" className="elina-text-link" onClick={onCancel}>{t("Annuler la lecture")}</button>}
  </div>;
}

